import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, Plus, Trash2, Download, Loader2, Mail, RefreshCw, AlertCircle, CheckCircle2, Table2
} from 'lucide-react';
import { format, subDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [7, 15, 30, 60, 90];

const STATUS_BADGE = {
  processing: { label: 'Processando', variant: 'secondary', icon: Loader2 },
  ready: { label: 'Pronto', variant: 'default', icon: CheckCircle2 },
  error: { label: 'Erro', variant: 'destructive', icon: AlertCircle },
};

export function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [orgGroups, setOrgGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [resendTarget, setResendTarget] = useState(null);
  const [resendEmails, setResendEmails] = useState('');
  const [form, setForm] = useState({
    org_id: '', group_ids: [], preset: 30,
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
    formats: ['pdf', 'csv'], include_ai: true, name: 'Relatório Gerencial',
  });
  const { getAuthHeader, user } = useAuthStore();
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const fetchReports = useCallback(async () => {
    try {
      const params = {};
      if (filterOrg !== 'all') params.org_id = filterOrg;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSource !== 'all') params.source = filterSource;
      const res = await axios.get(`${API}/reports`, { headers: authRef.current(), params });
      setReports(res.data);
    } catch (_) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  }, [filterOrg, filterStatus, filterSource]);

  useEffect(() => {
    fetchReports();
    axios.get(`${API}/orgs`, { headers: authRef.current() })
      .then(res => setOrgs(res.data)).catch(() => {});
    axios.get(`${API}/hostgroups`, { headers: authRef.current() })
      .then(res => setAllGroups((res.data.groups || []).map(g => ({ zabbix_group_id: g.id, group_name: g.name }))))
      .catch(() => {});
  }, [fetchReports]);

  useEffect(() => {
    const hasProcessing = reports.some(r => r.status === 'processing');
    if (!hasProcessing) return;
    const timer = setInterval(fetchReports, 10000);
    return () => clearInterval(timer);
  }, [reports, fetchReports]);

  const selectOrg = async (orgId) => {
    setForm(f => ({ ...f, org_id: orgId, group_ids: [] }));
    try {
      const res = await axios.get(`${API}/orgs/${orgId}`, { headers: authRef.current() });
      setOrgGroups(res.data.groups || []);
      const org = orgs.find(o => o.id === orgId);
      setForm(f => ({ ...f, name: `Relatório Gerencial — ${org?.display_name || org?.name || ''}` }));
    } catch (_) { setOrgGroups([]); }
  };

  const applyPreset = (days) => {
    setForm(f => ({
      ...f, preset: days,
      start: format(subDays(new Date(), days), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }));
  };

  const toggleFormat = (fmt) => {
    setForm(f => ({
      ...f,
      formats: f.formats.includes(fmt) ? f.formats.filter(x => x !== fmt) : [...f.formats, fmt],
    }));
  };

  const generate = async () => {
    if (!form.org_id && form.group_ids.length === 0) {
      toast.error('Selecione um Cliente/Org ou grupos de hosts');
      return;
    }
    if (form.formats.length === 0) { toast.error('Selecione ao menos um formato'); return; }
    setGenerating(true);
    try {
      await axios.post(`${API}/reports/generate`, {
        org_id: form.org_id || null,
        group_ids: form.group_ids,
        start: form.start, end: form.end,
        formats: form.formats, include_ai: form.include_ai, name: form.name,
      }, { headers: authRef.current() });
      toast.success('Relatório em processamento — ele aparecerá como "Pronto" ao finalizar');
      setDialogOpen(false);
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar relatório');
    } finally {
      setGenerating(false);
    }
  };

  const download = async (report, fmt) => {
    try {
      const res = await axios.get(`${API}/reports/${report.id}/download/${fmt}`, {
        headers: authRef.current(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', report.files[fmt]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao baixar arquivo');
    }
  };

  const retry = async (report) => {
    await axios.post(`${API}/reports/${report.id}/retry`, {}, { headers: authRef.current() });
    toast.info('Geração reiniciada');
    fetchReports();
  };

  const remove = async (report) => {
    if (!window.confirm('Excluir este relatório?')) return;
    await axios.delete(`${API}/reports/${report.id}`, { headers: authRef.current() });
    toast.success('Relatório excluído');
    fetchReports();
  };

  const resend = async () => {
    const recipients = resendEmails.split(',').map(e => e.trim()).filter(Boolean);
    if (recipients.length === 0) { toast.error('Informe ao menos um e-mail'); return; }
    try {
      const res = await axios.post(`${API}/reports/${resendTarget.id}/resend`, { recipients },
        { headers: authRef.current() });
      toast.success(res.data.message);
      setResendTarget(null); setResendEmails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar');
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 w-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Biblioteca de Relatórios</h2>
          <p className="text-sm text-muted-foreground">
            Relatórios manuais, agendados e automáticos — PDF e CSV com análise por IA
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger className="w-[190px]" data-testid="filter-org-select">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os clientes</SelectItem>
              {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]" data-testid="filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="processing">Processando</SelectItem>
              <SelectItem value="ready">Pronto</SelectItem>
              <SelectItem value="error">Erro</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-[150px]" data-testid="filter-source-select">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="scheduled">Agendado</SelectItem>
              <SelectItem value="dossier">Dossiê de domínio</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchReports} data-testid="refresh-reports-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="new-report-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo Relatório
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {reports.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-1">Nenhum relatório</h3>
              <p className="text-sm text-muted-foreground">
                Clique em &quot;Novo Relatório&quot;, escolha o cliente, os grupos e o período
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Grupos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tamanho</TableHead>
                  <TableHead>Gerado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map(r => {
                  const st = STATUS_BADGE[r.status] || STATUS_BADGE.processing;
                  return (
                    <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                      <TableCell className="font-medium">{r.org_name || 'Todos'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {r.source === 'scheduled' ? 'Agendado' : r.source === 'dossier' ? 'Dossiê' : 'Manual'}
                        </Badge>
                        {r.type && r.type !== 'Relatório Gerencial' && (
                          <div className="text-xs text-muted-foreground mt-1 max-w-[200px] truncate">{r.type}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{r.period_start} a {r.period_end}
                        <div className="text-xs text-muted-foreground">{r.period_days} dias</div></TableCell>
                      <TableCell className="text-sm">{(r.group_names || []).length || r.group_ids?.length || 0}</TableCell>
                      <TableCell>
                        <Badge variant={st.variant} className="gap-1">
                          <st.icon className={`h-3 w-3 ${r.status === 'processing' ? 'animate-spin' : ''}`} />
                          {st.label}
                        </Badge>
                        {r.status === 'error' && (
                          <div className="text-xs text-destructive mt-1 max-w-[220px]">{r.error}</div>
                        )}
                        {r.status === 'ready' && r.ai_included === false && r.source !== 'dossier' && (
                          <div className="text-xs text-muted-foreground mt-1">sem seções de IA</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.size_bytes ? `${Math.round(r.size_bytes / 1024)} KB` : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(r.created_at).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.files?.pdf && (
                          <Button variant="ghost" size="icon" title="Baixar PDF" onClick={() => download(r, 'pdf')}
                            data-testid={`download-pdf-${r.id}`}><Download className="h-4 w-4" /></Button>
                        )}
                        {r.files?.csv && (
                          <Button variant="ghost" size="icon" title="Baixar CSV" onClick={() => download(r, 'csv')}
                            data-testid={`download-csv-${r.id}`}><Table2 className="h-4 w-4" /></Button>
                        )}
                        {r.status === 'ready' && (
                          <Button variant="ghost" size="icon" title="Enviar por e-mail"
                            onClick={() => setResendTarget(r)} data-testid={`resend-${r.id}`}>
                            <Mail className="h-4 w-4" />
                          </Button>
                        )}
                        {r.status === 'error' && (
                          <Button variant="ghost" size="icon" title="Tentar novamente" onClick={() => retry(r)}
                            data-testid={`retry-${r.id}`}><RefreshCw className="h-4 w-4" /></Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => remove(r)}
                          data-testid={`delete-report-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New report */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[88vh]">
          <DialogHeader>
            <DialogTitle>Novo Relatório</DialogTitle>
            <DialogDescription>
              Seleção em dois níveis: primeiro o Cliente/Org, depois os grupos de hosts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            {user?.role !== 'client' && (
              <div className="space-y-2">
                <Label>Cliente/Org *</Label>
                <Select value={form.org_id} onValueChange={selectOrg}>
                  <SelectTrigger data-testid="report-org-select"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                  <SelectContent>
                    {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Grupos de Hosts</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="report-groups-btn">
                    {form.group_ids.length > 0
                      ? `${form.group_ids.length} grupo(s) selecionado(s)`
                      : 'Todos os grupos do cliente'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0">
                  <ScrollArea className="h-64 p-4">
                    {(orgGroups.length > 0 ? orgGroups : allGroups).length > 0 ? (
                      <div className="space-y-2">
                        {(orgGroups.length > 0 ? orgGroups : allGroups).map(g => (
                          <div key={g.zabbix_group_id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`rg-${g.zabbix_group_id}`}
                              checked={form.group_ids.includes(g.zabbix_group_id)}
                              onCheckedChange={() => setForm(f => ({
                                ...f,
                                group_ids: f.group_ids.includes(g.zabbix_group_id)
                                  ? f.group_ids.filter(x => x !== g.zabbix_group_id)
                                  : [...f.group_ids, g.zabbix_group_id],
                              }))}
                            />
                            <label htmlFor={`rg-${g.zabbix_group_id}`} className="text-sm cursor-pointer">{g.group_name}</label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Nenhum grupo disponível — verifique a configuração do Zabbix
                      </p>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Vazio = todos os grupos do cliente. Sem cliente vinculado, escolha os grupos direto do Zabbix.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Período</Label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map(d => (
                  <Button key={d} size="sm" variant={form.preset === d ? 'default' : 'outline'}
                    onClick={() => applyPreset(d)} data-testid={`preset-${d}`}>
                    {d} dias
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="space-y-1">
                  <Label className="text-xs">Início</Label>
                  <Input type="date" value={form.start}
                    onChange={e => setForm(f => ({ ...f, start: e.target.value, preset: 0 }))}
                    data-testid="report-start-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fim</Label>
                  <Input type="date" value={form.end}
                    onChange={e => setForm(f => ({ ...f, end: e.target.value, preset: 0 }))}
                    data-testid="report-end-input" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Períodos longos são processados em segundo plano e podem levar alguns minutos.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Formatos</Label>
              <div className="flex gap-4">
                {['pdf', 'csv'].map(fmt => (
                  <div key={fmt} className="flex items-center gap-2">
                    <Checkbox id={`fmt-${fmt}`} checked={form.formats.includes(fmt)}
                      onCheckedChange={() => toggleFormat(fmt)} data-testid={`format-${fmt}`} />
                    <label htmlFor={`fmt-${fmt}`} className="text-sm uppercase cursor-pointer">{fmt}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Enriquecimento por IA</Label>
                <p className="text-xs text-muted-foreground">Sumário executivo, ofensores e recomendações</p>
              </div>
              <Switch checked={form.include_ai}
                onCheckedChange={v => setForm(f => ({ ...f, include_ai: v }))} data-testid="report-ai-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={generate} disabled={generating} data-testid="generate-report-btn">
              {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Gerar Relatório
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend */}
      <Dialog open={!!resendTarget} onOpenChange={() => setResendTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar relatório por e-mail</DialogTitle>
            <DialogDescription>Os arquivos disponíveis seguem em anexo</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Destinatários (separados por vírgula)</Label>
            <Input value={resendEmails} onChange={e => setResendEmails(e.target.value)}
              placeholder="gestor@empresa.com" data-testid="resend-emails-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendTarget(null)}>Cancelar</Button>
            <Button onClick={resend} data-testid="confirm-resend-btn">Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
